import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BASE_URL, PERMISSIONS } from '../../../../constants';
import { Tooltip } from '@mui/material';
import { resetAuthState } from '../../../../redux/authSlice';
import Notifications from "../../../components/Notifications";
import { displayNotification, getUserTimeZone, handleRequest } from "helpers";
import RenderWithPermissions from "components/RenderWithPermissions";
import {
    LayoutDashboard,
    Activity,
    ClipboardList,
    Users,
    Shield,
    Database,
    Home,
    LogOut,
    Settings,
    FileSpreadsheet,
    Menu,
    X,
    ExternalLink
} from "lucide-react";

const partnerLogos = [
    {
        name: "STP TB Reach",
        src: "https://static.wixstatic.com/media/ed7121_446cb58ea3ce48d2b5ceca667b0154df~mv2.png/v1/fill/w_152,h_36,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/STP%20TB%20Reach.png",
        url: "https://www.stoptb.org/",
        height: 22,
    },
    {
        name: "Dopasi Foundation",
        src: "https://static.wixstatic.com/shapes/ed7121_03042b17cb1746aeb9ee0607f70793be.svg",
        url: "https://dopasi.org",
        height: 22,
    },
    {
        name: "CMU",
        src: "https://static.wixstatic.com/media/ed7121_8111c37594c14001806d49c0b927ebfd~mv2.png",
        height: 24,
    },
    {
        name: "National TB Control Program",
        src: "https://static.wixstatic.com/media/ed7121_27a54c0db55a403d8ce27513b914a2fd~mv2.png/v1/fill/w_109,h_81,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/National%20TB%20Control%20Program.png",
        height: 26,
    },
    {
        name: "CDC Sindh",
        src: "https://static.wixstatic.com/media/ed7121_41ebc41effb745fd99183ceacd94fbba~mv2.png/v1/fill/w_65,h_105,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/CDC%20Sindh.png",
        height: 26,
    },
    {
        name: "PTP Punjab",
        src: "https://static.wixstatic.com/media/ed7121_bda07bca0d2744ef89002fd9f71ac94e~mv2.png/v1/fill/w_90,h_95,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/PTP%20Punjab.png",
        height: 26,
    },
    {
        name: "TB Control Programme Balochistan",
        src: "https://static.wixstatic.com/media/ed7121_56929213a09b470c94595ba64f3cce6a~mv2.png/v1/fill/w_95,h_70,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/TB%20Control%20Programme%20Balochistan.png",
        height: 24,
    },
];

const Header = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const authData = useSelector((s) => s.auth.data);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const logout = async () => {
        try {
            const res = await handleRequest("/users/logout", "POST");
            if (res.success && res.status === 200) {
                dispatch(resetAuthState());
                navigate("/login");
            } else {
                displayNotification("error", "Failed to logout.");
            }
        } catch (error) {
            console.error(error);
            displayNotification("error", "Failed to logout.");
        }
    };

    const handleLogout = async () => {
        if (window.confirm("You are about to logout from eRespiroConnect. Are you sure?")) {
            await logout();
        }
    };

    const currentPath = location.pathname;
    const isActive = (path) => {
        if (path === "/portal") {
            return currentPath === "/portal" || currentPath === "/";
        }
        return currentPath.startsWith(path);
    };

    return (
        <header className="portal-header sticky-top">
            {/* Top Tier: Brand, Partner Logos, User Profile */}
            <div className="container-fluid px-lg-4 px-3 py-2">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    {/* Brand Insignia */}
                    <div className="d-flex align-items-center gap-3">
                        <Link
                            to={authData?.id ? "/portal" : "/"}
                            className="portal-brand-badge text-decoration-none"
                            title="eRespiroConnect Health Data Portal"
                        >
                            <img
                                src={`${BASE_URL}/assets/icons/logo.jpg`}
                                height="42"
                                alt="eRespiroConnect Logo"
                                style={{ objectFit: "contain" }}
                            />
                            <div className="d-none d-sm-block text-start border-start ps-2" style={{ borderColor: '#E2E8F0' }}>
                                <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#8B0304", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                                    eRespiroConnect
                                </div>
                                <div style={{ fontSize: "0.68rem", fontWeight: 600, color: "#64748B", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                                    Data & Surveillance Portal
                                </div>
                            </div>
                        </Link>

                        <div className="d-none d-xl-flex align-items-center gap-2 px-2 py-1 rounded-pill" style={{ background: "rgba(255, 255, 255, 0.12)", border: "1px solid rgba(255, 255, 255, 0.2)" }}>
                            <span className="portal-pulse-dot"></span>
                            <span className="text-white" style={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.04em" }}>
                                LIVE SURVEILLANCE
                            </span>
                        </div>
                    </div>

                    {/* Partner Logos Bar (Desktop) */}
                    <div className="d-none d-lg-flex align-items-center gap-1 portal-partner-cluster">
                        {partnerLogos.map((logo, idx) => (
                            <Tooltip key={idx} title={logo.name} arrow>
                                <div className="portal-partner-item">
                                    <img
                                        src={logo.src}
                                        alt={logo.name}
                                        height={logo.height}
                                        style={{ maxWidth: 85, objectFit: "contain" }}
                                    />
                                </div>
                            </Tooltip>
                        ))}
                    </div>

                    {/* Right User Bar or Login Button */}
                    {authData && authData.id ? (
                        <div className="d-flex align-items-center gap-2">
                            {/* Profile & Session Widget */}
                            <div className="portal-user-profile text-white">
                                <div className="d-none d-md-flex flex-column text-end" style={{ lineHeight: 1.2 }}>
                                    <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>
                                        {authData?.user_name ?? "Health Specialist"}
                                    </span>
                                    <div className="d-flex align-items-center justify-content-end gap-1 mt-1">
                                        <span
                                            className="badge rounded-pill text-white px-2"
                                            style={{ fontSize: "0.62rem", background: "rgba(255, 255, 255, 0.25)", fontWeight: 600 }}
                                        >
                                            {authData?.role_name ?? "Surveillance User"}
                                        </span>
                                        <Tooltip title="System Timezone">
                                            <span style={{ fontSize: "0.65rem", opacity: 0.85, cursor: "help" }}>
                                                • {getUserTimeZone()}
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>

                                <Tooltip title="User Profile">
                                    <Link to="/portal/profile" className="portal-avatar-wrap text-decoration-none">
                                        <img
                                            src={`${BASE_URL}/assets/icons/avatar.png`}
                                            alt="user"
                                            className="portal-avatar-img"
                                        />
                                        <span className="portal-avatar-badge" title="Active Session"></span>
                                    </Link>
                                </Tooltip>

                                <div className="portal-header-btn-group">
                                    <Notifications />

                                    {/* <Tooltip title="Profile & Settings">
                                        <Link
                                            to="/portal/profile"
                                            className="portal-header-icon-btn"
                                            aria-label="Profile and Settings"
                                        >
                                            <Settings size={16} />
                                        </Link>
                                    </Tooltip> */}

                                    <Tooltip title="Logout from Portal">
                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="portal-header-icon-btn logout-btn"
                                            aria-label="Logout"
                                        >
                                            <LogOut size={16} />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>

                            {/* Mobile Hamburger Toggle */}
                            <button
                                className="btn btn-sm text-white d-xl-none p-2"
                                style={{ background: "rgba(255,255,255,0.15)", borderRadius: 8 }}
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            >
                                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                            </button>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center gap-2">
                            <a
                                href="https://dopasi.org"
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm text-white d-none d-sm-inline-flex"
                                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)" }}
                            >
                                Dopasi Foundation <ExternalLink size={14} className="ms-1" />
                            </a>
                            <Link
                                to="/login"
                                className="btn btn-sm px-3 fw-bold text-dark bg-white shadow-sm"
                                style={{ borderRadius: 8 }}
                            >
                                Sign In
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Tier: Portal Module Navigation (Active when Logged In) */}
            {authData && authData.id && (
                <nav className={`portal-nav-bar ${mobileMenuOpen ? "d-block" : "d-none d-xl-block"}`}>
                    <div className="container-fluid px-lg-4 px-3">
                        <div className="d-flex flex-column flex-xl-row align-items-stretch justify-content-between overflow-x-auto py-1">
                            <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-1">
                                <Link
                                    to="/portal"
                                    className={`portal-nav-link ${isActive("/portal") && currentPath === "/portal" ? "active" : ""}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <Home size={16} />
                                    <span>Portal Hub</span>
                                </Link>

                                <RenderWithPermissions permissions={PERMISSIONS.qualitative.permissions}>
                                    <Link
                                        to="/portal/qualitative"
                                        className={`portal-nav-link ${currentPath === "/portal/qualitative" ? "active" : ""}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <LayoutDashboard size={16} />
                                        <span>Dashboards & Analytics</span>
                                    </Link>
                                </RenderWithPermissions>

                                <RenderWithPermissions permissions={PERMISSIONS.acfActivity.permissions}>
                                    <Link
                                        to="/portal/acf-activities"
                                        className={`portal-nav-link ${isActive("/portal/acf-activities") ? "active" : ""}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Activity size={16} />
                                        <span>ACF Activities</span>
                                    </Link>
                                </RenderWithPermissions>

                                <RenderWithPermissions permissions={PERMISSIONS.qualitative.permissions}>
                                    <Link
                                        to="/portal/qualitative/line-listing-acf-activities"
                                        className={`portal-nav-link ${isActive("/portal/qualitative/line-listing") ? "active" : ""}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <FileSpreadsheet size={16} />
                                        <span>Line Listing</span>
                                    </Link>
                                </RenderWithPermissions>

                                <RenderWithPermissions permissions={PERMISSIONS.metadata.permissions}>
                                    <Link
                                        to="/portal/meta-data"
                                        className={`portal-nav-link ${isActive("/portal/meta-data") ? "active" : ""}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Database size={16} />
                                        <span>Master Metadata</span>
                                    </Link>
                                </RenderWithPermissions>

                                <RenderWithPermissions permissions={PERMISSIONS.users.permissions}>
                                    <Link
                                        to="/portal/user-management"
                                        className={`portal-nav-link ${isActive("/portal/user-management") ? "active" : ""}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Users size={16} />
                                        <span>User Directory</span>
                                    </Link>
                                </RenderWithPermissions>

                                <RenderWithPermissions permissions={PERMISSIONS.roles.permissions}>
                                    <Link
                                        to="/portal/role-management"
                                        className={`portal-nav-link ${isActive("/portal/role-management") || isActive("/portal/permissions") ? "active" : ""}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Shield size={16} />
                                        <span>Roles & Access</span>
                                    </Link>
                                </RenderWithPermissions>
                            </div>

                            <div className="d-flex align-items-center gap-2 py-2 py-xl-0 border-top border-xl-0 text-muted" style={{ fontSize: "0.78rem" }}>
                                <span className="d-none d-xxl-inline">Stop TB Partnership • TB REACH Wave 11</span>
                            </div>
                        </div>
                    </div>
                </nav>
            )}
        </header>
    );
};

export default Header;
